"use client";

import { c as _c } from "react/compiler-runtime";
import { Select } from "@base-ui/react/select";
import { createRadiusOptions, useLocateUser } from "@repo/design-system/components/filters/geo-select";
import { useGeoapify } from "@repo/design-system/hooks/use-geoapify";
import { useGeoSearch } from "@repo/design-system/hooks/use-geosearch";
import { useLocale } from "@repo/design-system/hooks/use-locale";
import type { GeoLocation } from "@repo/design-system/types/geo";
import type { GeoapifyFeature } from "@repo/design-system/types/geoapify";
import { cn } from "@repo/design-system/utils/cn";
import { ChevronDown, Locate } from "lucide-react";
import { useState } from "react";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxFloatingLabel, ComboboxInput, ComboboxInputGroup, ComboboxItem, ComboboxList } from "~/components/ui/combobox-eon";
import { useLanguage } from "~/translations/loader/use-language";
interface GeoOption {
  feature?: GeoapifyFeature;
  geoLocation: GeoLocation;
  value: string;
}
export function GeoSelect(t0) {
  const $ = _c(72);
  const {
    label
  } = t0;
  const t = useLanguage();
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [resultType, setResultType] = useState();
  let t1;
  if ($[0] !== resultType || $[1] !== t.filterbar_geosearch_boundaries_city || $[2] !== t.filterbar_geosearch_boundaries_country || $[3] !== t.filterbar_geosearch_boundaries_region || $[4] !== t.filterbar_geosearch_unit) {
    t1 = createRadiusOptions({
      city: t.filterbar_geosearch_boundaries_city,
      country: t.filterbar_geosearch_boundaries_country,
      default: t.filterbar_geosearch_boundaries_region,
      unit: t.filterbar_geosearch_unit
    }, resultType);
    $[0] = resultType;
    $[1] = t.filterbar_geosearch_boundaries_city;
    $[2] = t.filterbar_geosearch_boundaries_country;
    $[3] = t.filterbar_geosearch_boundaries_region;
    $[4] = t.filterbar_geosearch_unit;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const radiusOptions = t1;
  const defaultRadius = radiusOptions[2];
  const boundariesRadius = radiusOptions[0];
  let t2;
  if ($[6] !== input || $[7] !== locale) {
    t2 = {
      type: "geo",
      query: input,
      lang: locale
    };
    $[6] = input;
    $[7] = locale;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    data: geoapifyData,
    isLoading: isLoadingGeoapify
  } = useGeoapify(t2);
  let t3;
  if ($[9] !== boundariesRadius || $[10] !== defaultRadius || $[11] !== geoapifyData || $[12] !== radiusOptions || $[13] !== resultType) {
    t3 = {
      geosearchQueryData: geoapifyData,
      radiusOptions,
      radiusOptionDefault: defaultRadius,
      radiusOptionBoundaries: boundariesRadius,
      resultType
    };
    $[9] = boundariesRadius;
    $[10] = defaultRadius;
    $[11] = geoapifyData;
    $[12] = radiusOptions;
    $[13] = resultType;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const {
    geoLocation,
    setGeoLocation,
    isLoadingBoundaries,
    radius,
    setRadius,
    filteredOptions,
    handleGeoSelectChange
  } = useGeoSearch(t3);
  let t4;
  if ($[15] !== defaultRadius || $[16] !== radius || $[17] !== setGeoLocation) {
    t4 = location => {
      const newRadius = radius.value === 0 ? defaultRadius : undefined;
      setGeoLocation(location, newRadius);
    };
    $[15] = defaultRadius;
    $[16] = radius;
    $[17] = setGeoLocation;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== locale || $[20] !== t4) {
    t5 = {
      locale,
      onSuccess: t4
    };
    $[19] = locale;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const {
    mutate: handleLocateUser,
    isPending: isLocatingUser,
    error
  } = useLocateUser(t5);
  const isLoading = isLoadingGeoapify || isLoadingBoundaries || isLocatingUser;
  const floating = open || isLocatingUser || !!geoLocation?.name;
  let t6;
  if ($[22] !== handleGeoSelectChange) {
    t6 = option => {
      if (!option) {
        return;
      }
      setResultType(option.feature?.properties.result_type);
      handleGeoSelectChange(option);
      setOpen(false);
    };
    $[22] = handleGeoSelectChange;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const handleOptionSelect = t6;
  let t7;
  if ($[24] !== floating || $[25] !== label) {
    t7 = <ComboboxFloatingLabel floating={floating} htmlFor="geo-select">{label}</ComboboxFloatingLabel>;
    $[24] = floating;
    $[25] = label;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== floating || $[28] !== geoLocation || $[29] !== open || $[30] !== t.filterbar_geosearch_placeholder) {
    t8 = geoLocation && !open ? <button className="flex-1 cursor-pointer px-3 text-left text-lg" onClick={e => {
      e.stopPropagation();
      setOpen(true);
    }} type="button">{geoLocation.name}</button> : <ComboboxInput id="geo-select" placeholder={floating ? t.filterbar_geosearch_placeholder : ""} />;
    $[27] = floating;
    $[28] = geoLocation;
    $[29] = open;
    $[30] = t.filterbar_geosearch_placeholder;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const t9 = t.filterbar_geosearch_user_location_button_title;
  let t10;
  if ($[32] !== handleLocateUser) {
    t10 = e_0 => {
      e_0.stopPropagation();
      handleLocateUser();
    };
    $[32] = handleLocateUser;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const t11 = isLocatingUser && "animate-pulse";
  let t12;
  if ($[34] !== t11) {
    t12 = cn("size-5 opacity-90 transition-opacity hover:opacity-50", t11);
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t12) {
    t13 = <Locate className={t12} color="currentColor" />;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t.filterbar_geosearch_user_location_button_title || $[39] !== t10 || $[40] !== t13) {
    t14 = <button aria-label={t9} className="mr-2 shrink-0 rounded-full p-1 hover:bg-white/10" onClick={t10} type="button">{t13}</button>;
    $[38] = t.filterbar_geosearch_user_location_button_title;
    $[39] = t10;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== open || $[43] !== t14 || $[44] !== t8) {
    t15 = <ComboboxInputGroup open={open}>{t8}{t14}</ComboboxInputGroup>;
    $[42] = open;
    $[43] = t14;
    $[44] = t8;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const t16 = isLoading ? t.filterbar_geosearch_loading : t.filterbar_geosearch_error;
  let t17;
  if ($[46] !== t16) {
    t17 = <ComboboxEmpty>{t16}</ComboboxEmpty>;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ComboboxList>{_temp2}</ComboboxList>;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t17) {
    t19 = <ComboboxContent>{t17}{t18}</ComboboxContent>;
    $[49] = t17;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== filteredOptions || $[52] !== handleOptionSelect || $[53] !== open || $[54] !== t15 || $[55] !== t19) {
    t20 = <Combobox filter={null} items={filteredOptions} itemToStringLabel={_temp} onInputValueChange={setInput} onOpenChange={setOpen} onValueChange={handleOptionSelect} open={open} value={null}>{t15}{t19}</Combobox>;
    $[51] = filteredOptions;
    $[52] = handleOptionSelect;
    $[53] = open;
    $[54] = t15;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== t20 || $[58] !== t7) {
    t21 = <div className="relative flex-1">{t7}{t20}</div>;
    $[57] = t20;
    $[58] = t7;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== geoLocation || $[61] !== radius || $[62] !== radiusOptions || $[63] !== setRadius) {
    t22 = geoLocation && <RadiusSelect options={radiusOptions} radius={radius} setRadius={setRadius} />;
    $[60] = geoLocation;
    $[61] = radius;
    $[62] = radiusOptions;
    $[63] = setRadius;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== error || $[66] !== t.filterbar_geosearch_locating_error) {
    t23 = error && <div className="border border-white p-2 text-lg text-white">{t.filterbar_geosearch_locating_error}</div>;
    $[65] = error;
    $[66] = t.filterbar_geosearch_locating_error;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== t21 || $[69] !== t22 || $[70] !== t23) {
    t24 = <div className="flex items-center gap-4">{t21}{t22}{t23}</div>;
    $[68] = t21;
    $[69] = t22;
    $[70] = t23;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  return t24;
}
function _temp2(option_0) {
  return <ComboboxItem className="text-sm" key={option_0.value} value={option_0}><p>{option_0.feature?.properties.city}</p><p>{`${option_0.feature?.properties.state ?? option_0.feature?.properties.city}, ${option_0.feature?.properties.country}`}</p></ComboboxItem>;
}
function _temp(opt) {
  return opt.feature?.properties.city || opt.value;
}
function RadiusSelect(t0) {
  const $ = _c(17);
  const {
    options,
    radius,
    setRadius
  } = t0;
  let t1;
  if ($[0] !== options || $[1] !== setRadius) {
    t1 = val => {
      const option = options.find(v => v.value === val);
      if (option) {
        setRadius(option);
      }
    };
    $[0] = options;
    $[1] = setRadius;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = radius.value;
  let t3;
  if ($[3] !== radius.label) {
    t3 = <Select.Value>{radius.label}</Select.Value>;
    $[3] = radius.label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Select.Icon><ChevronDown className="size-4" /></Select.Icon>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <Select.Trigger className="flex h-12 items-center gap-2 border border-white bg-transparent px-3 text-lg transition-colors">{t3}{t4}</Select.Trigger>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== options) {
    t6 = options.map(_temp3);
    $[8] = options;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <Select.Portal><Select.Positioner className="z-50" sideOffset={4}><Select.Popup className="border border-white bg-background shadow-[0_0_0_1px_white]"><Select.List>{t6}</Select.List></Select.Popup></Select.Positioner></Select.Portal>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== radius.value || $[13] !== t1 || $[14] !== t5 || $[15] !== t7) {
    t8 = <Select.Root onValueChange={t1} value={t2}>{t5}{t7}</Select.Root>;
    $[12] = radius.value;
    $[13] = t1;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function _temp3(opt) {
  return <Select.Item className="cursor-pointer px-3 py-2 text-sm data-[highlighted]:bg-muted data-[highlighted]:underline" key={opt.value} value={opt.value}><Select.ItemText>{opt.label}</Select.ItemText></Select.Item>;
}